import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Lock, CreditCard, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, eur, imgUrl, errMsg } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { useStore } from "../context/StoreContext";
import { LangBadge } from "../components/shop/Flag";
import { LaunchSignup } from "../components/shop/Launch";

const COUNTRIES = [["DE", "Deutschland"], ["AT", "Österreich"], ["EU", "EU (andere)"]];

const Field = ({ id, label, ...props }) => (
  <label className="block">
    <span className="text-xs text-[#707783]">{label}</span>
    <input id={id} data-testid={`checkout-${id}-input`} required {...props}
      className="mt-1 w-full rounded-xl border border-[#120E1F]/10 bg-white px-4 py-3 text-sm focus:outline-none focus:border-[#F2C230] transition-colors" />
  </label>
);

export default function Checkout() {
  const { items, subtotal, validate } = useCart();
  const { user } = useAuth();
  const { t, lang, pname } = useLang();
  const nav = useNavigate();
  const cfg = useStore();
  const [form, setForm] = useState({ email: "", name: "", street: "", zip: "", city: "", country: "DE" });
  const [method, setMethod] = useState("stripe");
  const [loading, setLoading] = useState(false);
  const [coupons, setCoupons] = useState([]);
  const [coupon, setCoupon] = useState("");

  useEffect(() => { if (user && user.role !== "admin") api.get("/community/me").then((r) => setCoupons(r.data.coupons.filter((c) => !c.used))).catch(() => setCoupons([])); }, [user]);
  useEffect(() => { if (user) setForm((f) => ({ ...f, email: f.email || user.email, name: f.name || user.name })); }, [user]);

  const ship = form.country === "DE" && subtotal >= (cfg.free_shipping_de ?? Infinity) ? 0 : cfg.shipping[form.country] ?? 12.99;
  const discount = Math.min(coupons.find((c) => c.code === coupon)?.amount || 0, subtotal);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { email, ...address } = form;
      const check = await validate();
      if (!check.ok) { toast.warning(lang === "en" ? "Your cart changed – please review it." : "Dein Warenkorb hat sich geändert – bitte prüfen."); setLoading(false); nav("/cart"); return; }
      const { data } = await api.post("/checkout", { items: items.map((i) => ({ product_id: i.id, quantity: i.quantity })),
        email, address, payment_method: method, origin_url: window.location.origin, lang, coupon_code: coupon || undefined });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(errMsg(err)); setLoading(false); }
  };

  if (cfg.prelaunch) return (
    <div className="py-32 bg-[#120E1F] grain text-center px-4" data-testid="checkout-coming-soon">
      <h1 className="font-display text-4xl sm:text-5xl text-white">{t("coming_soon")}</h1>
      <p className="text-white/60 mt-4 max-w-md mx-auto">{t("launch_text")}</p>
      <div className="mt-8 flex justify-center"><LaunchSignup testid="checkout-launch-signup" /></div>
    </div>
  );
  if (!items.length) return <div className="py-32 text-center bg-[#F7F5FB]"><p className="text-[#707783]">{t("cart_empty")}</p><Link to="/shop" className="btn-gold mt-6 inline-flex">{t("back_shop")}</Link></div>;

  return (
    <div className="bg-[#F7F5FB] min-h-screen">
      <form onSubmit={submit} className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid lg:grid-cols-5 gap-10" data-testid="checkout-form">
        <div className="lg:col-span-3 space-y-8">
          <h1 className="font-display text-4xl sm:text-5xl text-[#120E1F]">{t("checkout")}</h1>
          <section className="space-y-4"><h2 className="step-title">1 · {t("contact")}</h2>
            <Field id="email" type="email" label={t("email")} value={form.email} onChange={set("email")} />
          </section>
          <section className="space-y-4"><h2 className="step-title">2 · {t("ship_addr")}</h2>
            <Field id="name" label={t("name")} value={form.name} onChange={set("name")} />
            <Field id="street" label={t("street")} value={form.street} onChange={set("street")} />
            <div className="grid grid-cols-3 gap-4">
              <Field id="zip" label={t("zip")} value={form.zip} onChange={set("zip")} />
              <div className="col-span-2"><Field id="city" label={t("city")} value={form.city} onChange={set("city")} /></div>
            </div>
            <label className="block"><span className="text-xs text-[#707783]">{t("country")}</span>
              <select data-testid="checkout-country-select" value={form.country} onChange={set("country")} className="mt-1 w-full rounded-xl border border-[#120E1F]/10 bg-white px-4 py-3 text-sm">
                {COUNTRIES.map(([c, l]) => <option key={c} value={c}>{l}</option>)}
              </select>
            </label>
          </section>
          <section className="space-y-3"><h2 className="step-title">3 · {t("payment")}</h2>
            <PayOption id="stripe" active={method === "stripe"} onClick={() => setMethod("stripe")} title="Stripe" sub={t("pay_card")} icon={<CreditCard className="h-5 w-5" />} />
            <PayOption id="paypal" active={method === "paypal"} disabled={!cfg.paypal_enabled} onClick={() => setMethod("paypal")} title={t("pay_paypal")} sub={cfg.paypal_enabled ? "PayPal Checkout" : t("paypal_soon")} icon={<span className="font-bold italic text-[#003087]">P</span>} />
            <p className="text-xs text-[#707783] flex items-center gap-2"><Lock className="h-3.5 w-3.5" />{t("secure_note")}</p>
          </section>
        </div>
        <aside className="lg:col-span-2">
          <div className="lg:sticky lg:top-36 bg-[#120E1F] text-white rounded-[24px] p-6 space-y-5" data-testid="order-summary">
            <h2 className="font-display text-2xl">{t("order_summary")}</h2>
            <div className="space-y-3 max-h-72 overflow-y-auto">
              {items.map((i) => (
                <div key={i.id} className="flex gap-3 items-center">
                  <img src={imgUrl(i.image)} alt="" className="h-14 w-12 rounded-lg object-cover" />
                  <div className="flex-1 min-w-0"><p className="text-sm line-clamp-2">{pname(i)} <span className="text-white/50">× {i.quantity}</span></p>
                    <div className="flex flex-wrap items-center gap-2 mt-1"><LangBadge code={i.language} dark testid={`checkout-lang-${i.slug}`} />{i.sku && <span className="text-[10px] text-white/40 font-mono">SKU {i.sku}</span>}</div></div>
                  <span className="font-mono text-sm">{eur(i.price * i.quantity)}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-white/10 pt-4 space-y-2 text-sm">
              <div className="flex justify-between text-white/70"><span>{t("subtotal")}</span><span className="font-mono">{eur(subtotal)}</span></div>
              <div className="flex justify-between text-white/70"><span>{t("shipping")}</span><span className="font-mono" data-testid="summary-shipping">{ship === 0 ? t("free") : eur(ship)}</span></div>
              {coupons.length > 0 && (
                <select value={coupon} onChange={(e) => setCoupon(e.target.value)} data-testid="checkout-voucher-select" className="w-full rounded-lg bg-white/5 border border-white/15 px-3 py-2 text-xs text-white">
                  <option value="" className="text-black">{lang === "en" ? "Jolgen points voucher …" : "Jolgen Punkte-Gutschein …"}</option>
                  {coupons.map((c) => <option key={c.code} value={c.code} className="text-black">{`${c.code} · −${eur(c.amount)}`}</option>)}
                </select>
              )}
              {discount > 0 && <div className="flex justify-between text-[#F2C230]"><span>{lang === "en" ? "Voucher" : "Gutschein"}</span><span className="font-mono" data-testid="summary-discount">−{eur(discount)}</span></div>}
              <div className="flex justify-between text-lg pt-2"><span>{t("total")}</span><span className="font-mono text-[#C9B6F2]" data-testid="summary-total">{eur(subtotal - discount + ship)}</span></div>
              <p className="text-[10px] text-white/40">{lang === "en" ? "incl. VAT" : "inkl. MwSt."}</p>
            </div>
            <button type="submit" disabled={loading} data-testid="place-order-btn" className="btn-gold w-full h-12">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}{t("pay_now")}
            </button>
          </div>
        </aside>
      </form>
    </div>
  );
}

const PayOption = ({ id, active, disabled, onClick, title, sub, icon }) => (
  <button type="button" disabled={disabled} onClick={onClick} data-testid={`pay-method-${id}`}
    className={`w-full flex items-center gap-4 rounded-xl border px-4 py-4 text-left transition-colors ${active ? "border-[#F2C230] bg-[#F2C230]/5" : "border-[#120E1F]/10 bg-white"} disabled:opacity-40 disabled:cursor-not-allowed`}>
    <span className={`h-4 w-4 rounded-full border-2 ${active ? "border-[#F2C230] bg-[#F2C230]" : "border-[#120E1F]/20"}`} />
    <span className="flex-1"><span className="block text-sm font-semibold text-[#120E1F]">{title}</span><span className="text-xs text-[#707783]">{sub}</span></span>
    {icon}
  </button>
);
